import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
const AUDIT_API_URL =
  "https://rx5h93zyjk.execute-api.ap-south-1.amazonaws.com/default/AdminReconciliationFun";

const ITEMS_PER_PAGE = 10; // Only renders 10 complex cards at a time to keep DOM overhead near zero!

export default function TransactionAuditor() {
  const [anomalies, setAnomalies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [repairingId, setRepairingId] = useState(null);

  // Pagination State Controls
  const [currentPage, setCurrentPage] = useState(1);

  const runAuditScan = useCallback(async () => {
    setLoading(true);
    setAnomalies([]);
    setCurrentPage(1);

    try {
      const res = await fetch(AUDIT_API_URL, { method: "GET" });
      if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
      const data = await res.json();
      if (data.error) throw new Error(data.details || "Audit failed.");
      setAnomalies(data.anomalies || []);
    } catch (err) {
      console.error("Audit failed:", err);
      alert("Audit failed: " + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    runAuditScan();
  }, [runAuditScan]);

  const handleFixStatus = async (baseId, records, targetStatus) => {
    setRepairingId(baseId);
    try {
      const res = await fetch(AUDIT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actionType: "FIX_STATUS_MISMATCH",
          baseId,
          targetStatus,
          records,
        }),
      });
      if (!res.ok) throw new Error("Repair mutation transaction aborted.");
      alert(`Successfully synchronized status to: ${targetStatus}`);
      runAuditScan();
    } catch (err) {
      alert("Correction run failed: " + err.message);
    } finally {
      setRepairingId(null);
    }
  };

  const handleGenerateOpposite = async (baseId, orphanRecord, targetStatus) => {
    setRepairingId(baseId);
    try {
      const res = await fetch(AUDIT_API_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          actionType: "GENERATE_MISSING_OPPOSITE",
          baseId,
          targetStatus,
          orphanRecord,
        }),
      });
      if (!res.ok) throw new Error("Counterpart creation failed.");
      alert("Missing transaction pair row successfully generated.");
      runAuditScan();
    } catch (err) {
      alert("Creation run failed: " + err.message);
    } finally {
      setRepairingId(null);
    }
  };

  // OPTIMIZATION & REJECTED FILTER: Memoize filtered rows
  // OPTIMIZATION & ADVANCED FILTER INTEGRATION
  const filteredAnomalies = useMemo(() => {
    setCurrentPage(1); // Reset page view slot safely when filter state shifts

    return anomalies.filter((anomaly) => {
      // 1. CRITICAL GUARD: Drop the card if any record matches REJECTED, TP, COMM, or EXP
      const hasExcludedRecord = anomaly.records.some((r) => {
        if (!r.paymentId) return false;
        return (
          r.status === "REJECTED" ||
          r.paymentId.includes("TP") ||
          r.paymentId.includes("COMM") ||
          r.paymentId.includes("EXP") ||
          r.fromBranch === r.toBranch ||
          (r.fromBranch === "ADMIN" && r.toBranch === "UNKNOWN")
        );
      });

      if (hasExcludedRecord) return false;

      // 2. Standard Category Filter Rules
      if (statusFilter === "ALL") return true;
      if (statusFilter === "MISMATCH")
        return anomaly.type === "STATUS_MISMATCH";
      if (statusFilter === "ORPHAN") return anomaly.type.includes("ORPHAN");
      return true;
    });
  }, [anomalies, statusFilter]);

  // OPTIMIZATION: Extract only the 10 rows matching the active page segment view
  const paginatedAnomalies = useMemo(() => {
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredAnomalies.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredAnomalies, currentPage]);

  const totalPages = Math.ceil(filteredAnomalies.length / ITEMS_PER_PAGE) || 1;

  return (
    <div
      style={{
        padding: "24px",
        backgroundColor: "#f8f9fa",
        minHeight: "100vh",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "24px",
        }}
      >
        <div>
          <h1 style={{ margin: 0, fontSize: "24px", color: "#1a202c" }}>
            System Ledger Reconciliation Engine
          </h1>
          <p style={{ margin: "4px 0 0 0", color: "#4a5568" }}>
            Scan, verify, and repair structural entry drops across branch
            channels.
          </p>
        </div>
        <Link to="/">
          <button className="Branch-nav">Back to Dashboard</button>
        </Link>
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          {loading && (
            <span
              style={{
                fontSize: "13px",
                color: "#4a5568",
                backgroundColor: "#e2e8f0",
                padding: "6px 12px",
                borderRadius: "20px",
              }}
            >
              🔄 Scanning full ledger...
            </span>
          )}
          <button
            onClick={runAuditScan}
            disabled={loading}
            style={{
              padding: "10px 20px",
              backgroundColor: "#3182ce",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: loading ? "not-allowed" : "pointer",
              fontWeight: "bold",
            }}
          >
            {loading ? "Scanning Ledger..." : "Run Global Fresh Audit"}
          </button>
        </div>
      </div>

      {/* FILTER CONTROL SEGMENTS */}
      <div style={{ marginBottom: "16px", display: "flex", gap: "12px" }}>
        {["ALL", "MISMATCH", "ORPHAN"].map((mode) => (
          <button
            key={mode}
            onClick={() => setStatusFilter(mode)}
            style={{
              padding: "8px 16px",
              backgroundColor: statusFilter === mode ? "#2d3748" : "#edf2f7",
              color: statusFilter === mode ? "#fff" : "#2d3748",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "500",
            }}
          >
            {mode === "ALL" && `All Issues (${anomalies.length})`}
            {mode === "MISMATCH" &&
              `Status Mismatches (${anomalies.filter((a) => a.type === "STATUS_MISMATCH").length})`}
            {mode === "ORPHAN" &&
              `Missing Opposite Entries (${anomalies.filter((a) => a.type.includes("ORPHAN")).length})`}
          </button>
        ))}
      </div>

      {/* AUDIT DATA PRESENTATION LIST GRID */}
      {filteredAnomalies.length === 0 && !loading ? (
        <div
          style={{
            backgroundColor: "#fff",
            padding: "32px",
            textAlign: "center",
            borderRadius: "8px",
            border: "1px solid #e2e8f0",
            color: "#38a169",
            fontWeight: "bold",
          }}
        >
          💯 Clean Sheet! All dual transaction balance entries across all
          branches conform to ledger structure logic.
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          {paginatedAnomalies.map((anomaly) => (
            <div
              key={anomaly.baseId}
              style={{
                backgroundColor: "#fff",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
                padding: "16px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <span
                    style={{
                      padding: "4px 8px",
                      borderRadius: "4px",
                      fontSize: "12px",
                      fontWeight: "bold",
                      backgroundColor:
                        anomaly.type === "STATUS_MISMATCH"
                          ? "#feebc8"
                          : "#fed7d7",
                      color:
                        anomaly.type === "STATUS_MISMATCH"
                          ? "#c05621"
                          : "#9b2c2c",
                      marginRight: "8px",
                    }}
                  >
                    {anomaly.type}
                  </span>
                  <strong style={{ fontSize: "16px", color: "#2d3748" }}>
                    ID: {anomaly.baseId}
                  </strong>
                  <p
                    style={{
                      margin: "6px 0 0 0",
                      fontSize: "14px",
                      color: "#4a5568",
                    }}
                  >
                    {anomaly.description}
                  </p>
                </div>

                <div style={{ display: "flex", gap: "8px" }}>
                  {anomaly.type === "STATUS_MISMATCH" ? (
                    <>
                      <button
                        disabled={repairingId === anomaly.baseId}
                        onClick={() =>
                          handleFixStatus(
                            anomaly.baseId,
                            anomaly.records,
                            "APPROVED",
                          )
                        }
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#dd6b20",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        Force All to APPROVED
                      </button>
                      <button
                        disabled={repairingId === anomaly.baseId}
                        onClick={() =>
                          handleFixStatus(
                            anomaly.baseId,
                            anomaly.records,
                            "DELIVERED",
                          )
                        }
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#38a169",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        Force All to DELIVERED
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        disabled={repairingId === anomaly.baseId}
                        onClick={() =>
                          handleGenerateOpposite(
                            anomaly.baseId,
                            anomaly.records[0],
                            "APPROVED",
                          )
                        }
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#3182ce",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        Rebuild Counterpart Pair (APPROVED)
                      </button>
                      <button
                        disabled={repairingId === anomaly.baseId}
                        onClick={() =>
                          handleGenerateOpposite(
                            anomaly.baseId,
                            anomaly.records[0],
                            "DELIVERED",
                          )
                        }
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#48bb78",
                          color: "#fff",
                          border: "none",
                          borderRadius: "4px",
                          cursor: "pointer",
                          fontSize: "13px",
                        }}
                      >
                        Rebuild Counterpart Pair (DELIVERED)
                      </button>
                    </>
                  )}
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#f7fafc",
                  padding: "12px",
                  borderRadius: "6px",
                  border: "1px solid #edf2f7",
                }}
              >
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "13px",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        textAlign: "left",
                        color: "#718096",
                        borderBottom: "1px solid #e2e8f0",
                      }}
                    >
                      <th style={{ paddingBottom: "6px" }}>paymentId</th>
                      <th style={{ paddingBottom: "6px" }}>Date</th>
                      <th style={{ paddingBottom: "6px" }}>Type</th>
                      <th style={{ paddingBottom: "6px" }}>Role</th>
                      <th style={{ paddingBottom: "6px" }}>From</th>
                      <th style={{ paddingBottom: "6px" }}>To</th>
                      <th style={{ paddingBottom: "6px" }}>Amount</th>
                      <th style={{ paddingBottom: "6px" }}>Current Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anomaly.records.map((r, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom:
                            idx !== anomaly.records.length - 1
                              ? "1px solid #edf2f7"
                              : "none",
                          color: "#2d3748",
                        }}
                      >
                        <td
                          style={{ padding: "8px 0", fontFamily: "monospace" }}
                        >
                          {r.paymentId}
                        </td>
                        <td>{new Date(r.timestamp).toLocaleString()}</td>
                        <td>
                          <strong>{r.paymentType}</strong>
                        </td>
                        <td>{r.flowRole}</td>
                        <td>{r.fromBranch}</td>
                        <td>{r.toBranch}</td>
                        <td>₹{Number(r.amount).toLocaleString()}</td>
                        <td
                          style={{
                            color:
                              r.status === "PENDING" ? "#dd6b20" : "#38a169",
                            fontWeight: "bold",
                          }}
                        >
                          {r.status}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* FRONTEND PAGINATION CONTROLLER FOOTHUB */}
      {filteredAnomalies.length > ITEMS_PER_PAGE && (
        <div
          style={{
            marginTop: "24px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "16px",
          }}
        >
          <button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            style={{
              padding: "8px 16px",
              backgroundColor: currentPage === 1 ? "#cbd5e0" : "#2d3748",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: currentPage === 1 ? "not-allowed" : "pointer",
            }}
          >
            Previous
          </button>
          <span
            style={{ fontSize: "14px", color: "#4a5568", fontWeight: "500" }}
          >
            Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>{" "}
            (Filtered Pool: {filteredAnomalies.length})
          </span>
          <button
            disabled={currentPage === totalPages}
            onClick={() =>
              setCurrentPage((prev) => Math.min(prev + 1, totalPages))
            }
            style={{
              padding: "8px 16px",
              backgroundColor:
                currentPage === totalPages ? "#cbd5e0" : "#2d3748",
              color: "#fff",
              border: "none",
              borderRadius: "4px",
              cursor: currentPage === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
